import { useEffect, useId, useRef, useState } from 'react';
import { skipToken, useMutation, useQuery } from '@tanstack/react-query';
import type { inferRouterOutputs } from '@trpc/server';
import type { AppRouter } from '@servwell/api/router';
import { can, formatQty, type VanTemplateProposal } from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { createIntentKey } from '../../lib/idempotency';
import { fieldErrorAt, fieldErrorOf, numberTextIssue } from '../../lib/field-error';
import { useSession } from '../session';
import { Sheet } from '../ui/Sheet';
import { Button } from '../ui/Button';
import { Notice } from '../ui/Badge';
import { ErrorState, shownState, refreshFailed, StaleNotice } from '../ui/QueryStates';
import { Skeleton } from '../ui/Skeleton';
import { useOnline } from '../ui/useConnection';
import { Field, field } from '../create/Field';
import { IconInventory } from '../icons';
import { useTemplateReview, VanTemplatePreview } from './VanTemplatePreview';

type Template = inferRouterOutputs<AppRouter>['restocks']['template'];
const inputClass = `${field} min-h-11`;

export function VanTemplateSheet({
  templateId,
  onClose,
  onBusyChange,
}: {
  templateId?: string;
  onClose: () => void;
  onBusyChange?: (busy: boolean) => void;
}) {
  const trpc = useTRPC(),
    [id, setId] = useState(templateId ?? null),
    [offset, setOffset] = useState(0),
    [creating, setCreating] = useState(false);
  const q = useQuery(
    trpc.restocks.template.queryOptions(id ? { templateId: id, offset } : skipToken),
  );
  const review = useTemplateReview(onClose),
    locked = review.saving || creating;
  useEffect(() => {
    onBusyChange?.(locked);
    return () => onBusyChange?.(false);
  }, [locked, onBusyChange]);
  const state = shownState(q, q.data);
  return (
    <Sheet
      className="[&_button]:min-h-11 [&_button]:min-w-11"
      open
      wide
      title={q.data?.name ?? 'Create template'}
      onClose={() => {
        if (!locked) onClose();
      }}
    >
      {!id ? (
        <VanTemplateCreateForm
          onCreated={(next) => setId(next)}
          onBusyChange={setCreating}
        />
      ) : state === 'loading' ? (
        <Skeleton className="h-40" />
      ) : !q.data ? (
        <ErrorState
          icon={IconInventory}
          what="template"
          onRetry={() => void q.refetch()}
        />
      ) : (
        <>
          <StaleNotice show={refreshFailed(q, q.data)} onRetry={() => void q.refetch()} />
          <div hidden={!!review.state} className="space-y-4">
            <VanTemplateEditor
              key={id}
              template={q.data}
              saving={locked || !review.allowed}
              online={review.online}
              active={!review.state}
              error={review.draftError}
              onReview={(p) => void review.start(p)}
            />
            <section className="space-y-2 border-t border-border pt-4">
              <h2 className="text-sm font-semibold">
                Following vans · {q.data.followerCount}
              </h2>
              {!q.data.followerCount ? (
                <p className="text-sm text-muted-foreground">
                  No vans follow this template.
                </p>
              ) : null}
              <ul className="divide-y divide-border">
                {q.data.followers.map((f) => (
                  <li key={f.vanId} className="space-y-2 py-3">
                    <p className="break-words text-sm">
                      {f.name}
                      {!f.available ? ' · Unavailable; policy retained' : ''}
                    </p>
                    <Button
                      variant="ghost"
                      className="min-h-11"
                      disabled={locked || !review.online}
                      onClick={() =>
                        void review.start({ kind: 'detach', vanId: f.vanId })
                      }
                    >
                      Review detach {f.name}
                    </Button>
                  </li>
                ))}
              </ul>
              {q.data.followerCount > 25 ? (
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="ghost"
                    disabled={locked || offset === 0 || q.isFetching}
                    onClick={() => setOffset((n) => Math.max(0, n - 25))}
                  >
                    Previous vans
                  </Button>
                  <Button
                    variant="ghost"
                    disabled={locked || q.data.nextOffset === null || q.isFetching}
                    onClick={() => setOffset(q.data!.nextOffset!)}
                  >
                    Next vans
                  </Button>
                </div>
              ) : null}
            </section>
            <section className="space-y-2 border-t border-border pt-4">
              <p className="text-sm">
                Deleting this template preserves every follower's effective pairs and
                purchase packs. Stock and owed quantities stay in place.
              </p>
              <Button
                variant="danger"
                className="min-h-11"
                disabled={locked || !review.online}
                onClick={() => void review.start({ kind: 'delete', templateId: id })}
              >
                Review delete template
              </Button>
            </section>
          </div>
          {review.state ? (
            <VanTemplatePreview
              authorized={review.allowed}
              view={review.state.view}
              proposal={review.state.proposal}
              page={review.state.page}
              loading={review.state.loading}
              saving={review.saving}
              online={review.online}
              error={review.state.error}
              onConfirm={() => void review.confirm()}
              onPage={review.page}
              onPreserveLocal={review.preserve}
              onReload={review.reload}
              onBack={review.back}
            />
          ) : null}
        </>
      )}
    </Sheet>
  );
}

export function VanTemplateCreateForm({
  onCreated,
  onBusyChange,
}: {
  onCreated: (id: string) => void;
  onBusyChange?: (busy: boolean) => void;
}) {
  const trpc = useTRPC(),
    session = useSession(),
    online = useOnline();
  const [name, setName] = useState(''),
    [sourceKind, setSourceKind] = useState<'blank' | 'template' | 'van'>('blank'),
    [sourceId, setSourceId] = useState(''),
    [offset, setOffset] = useState(0),
    [keys] = useState(() => createIntentKey());
  const templates = useQuery(trpc.restocks.templates.queryOptions({ offset })),
    vans = useQuery(trpc.restocks.vans.queryOptions());
  const template = useQuery(
    trpc.restocks.template.queryOptions(
      sourceKind === 'template' && sourceId
        ? { templateId: sourceId, offset: 0 }
        : skipToken,
    ),
  );
  const van = useQuery(
    trpc.restocks.config.queryOptions(
      sourceKind === 'van' && sourceId ? { vanId: sourceId } : skipToken,
    ),
  );
  const save = useMutation(trpc.restocks.templateCreate.mutationOptions());
  const scope = `${session.orgId}:${session.userId}:${session.role}`,
    current = useRef(scope),
    alive = useRef(true);
  current.current = scope;
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  useEffect(() => {
    onBusyChange?.(save.isPending);
    return () => onBusyChange?.(false);
  }, [save.isPending, onBusyChange]);
  const source = sourceKind === 'template' ? template : van;
  const sourceReady =
    sourceKind === 'blank' ||
    (sourceId &&
      source.data &&
      !source.isFetching &&
      !source.error &&
      source.data.contractVersion === 2);
  const submit = async () => {
    if (
      !online ||
      save.isPending ||
      !name.trim() ||
      !sourceReady ||
      !can(session.role, 'inventory', 'manage')
    )
      return;
    const payload = {
      name,
      source:
        sourceKind === 'blank'
          ? { kind: 'blank' as const }
          : { kind: sourceKind, id: sourceId, expectedRevision: source.data!.revision },
    };
    try {
      const created = await save.mutateAsync({
        ...payload,
        ...keys.forIntent([scope, 'restocks.templateCreate', payload]),
      });
      if (alive.current && current.current === scope) {
        keys.clear();
        onCreated(created.templateId);
      }
    } catch {
      /* The mutation owns the visible error and unchanged retry key. */
    }
  };
  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <p className="text-sm text-muted-foreground">
        Copy Keep/Fill pairs from a template or the effective policy of a van. Copies do
        not assign vans or move stock.
      </p>
      <fieldset disabled={save.isPending} className="space-y-4">
        <Field label="Template name">
          <input
            className={inputClass}
            aria-label="Template name"
            required
            maxLength={120}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </Field>
        <Field label="Start from">
          <select
            className={inputClass}
            value={sourceKind}
            onChange={(e) => {
              setSourceKind(e.target.value as typeof sourceKind);
              setSourceId('');
              save.reset();
            }}
          >
            <option value="blank">Blank</option>
            <option value="template">Existing template</option>
            <option value="van">Effective van policy</option>
          </select>
        </Field>
        {sourceKind !== 'blank' ? (
          <Field label={sourceKind === 'template' ? 'Source template' : 'Source van'}>
            <select
              className={inputClass}
              value={sourceId}
              onChange={(e) => {
                setSourceId(e.target.value);
                save.reset();
              }}
            >
              <option value="">Choose a source</option>
              {sourceKind === 'template'
                ? templates.data?.rows.map((t) => (
                    <option key={t.templateId} value={t.templateId}>
                      {t.name}
                    </option>
                  ))
                : vans.data
                    ?.filter((v) => v.ownerAvailable)
                    .map((v) => (
                      <option key={v.vanId} value={v.vanId}>
                        {v.name} · {v.ownerName}
                      </option>
                    ))}
            </select>
          </Field>
        ) : null}
        {sourceKind === 'template' ? (
          <div className="flex flex-wrap gap-2">
            <Button
              variant="ghost"
              disabled={offset === 0 || templates.isFetching}
              onClick={() => setOffset((n) => Math.max(0, n - 25))}
            >
              Previous sources
            </Button>
            <Button
              variant="ghost"
              disabled={templates.data?.nextOffset == null || templates.isFetching}
              onClick={() => setOffset(templates.data!.nextOffset!)}
            >
              Next sources
            </Button>
          </div>
        ) : null}
      </fieldset>
      {sourceKind !== 'blank' && source.data ? (
        <section className="max-h-64 overflow-auto border border-border p-3 text-sm">
          <p>{source.data.points.length} copied pairs</p>
          <ul>
            {source.data.points.map((p) => (
              <li key={p.itemId}>
                {p.itemName} · Keep {formatQty(p.keepAtLeastHundredths)} · Fill{' '}
                {formatQty(p.fillToHundredths)}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {sourceKind !== 'blank' && source.error ? (
        <Notice tone="danger" alert>
          {source.error.message}
        </Notice>
      ) : null}
      {save.error ? (
        <Notice tone="danger" alert>
          {fieldErrorOf(save.error.message)}
        </Notice>
      ) : null}
      {!online ? <Notice tone="warn">Offline. Your inputs are kept.</Notice> : null}
      <Button
        type="submit"
        className="min-h-11 w-full"
        disabled={!online || save.isPending || !name.trim() || !sourceReady}
      >
        {save.isPending ? 'Creating…' : 'Create template'}
      </Button>
    </form>
  );
}

export function VanTemplateEditor({
  template,
  saving,
  online,
  onReview,
  error,
  active = true,
}: {
  error?: Error | null;
  active?: boolean;
  template: Template;
  saving: boolean;
  online: boolean;
  onReview: (proposal: VanTemplateProposal) => void;
}) {
  const [name, setName] = useState(template.name),
    [rows, setRows] = useState(() =>
      template.points.map((p) => ({
        ...p,
        keep: formatQty(p.keepAtLeastHundredths),
        fill: formatQty(p.fillToHundredths),
      })),
    ),
    [selected, setSelected] = useState(''),
    [page, setPage] = useState(0),
    [localError, setLocalError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState<string[]>([]),
    root = useRef<HTMLElement>(null);
  const fieldsId = useId();
  useEffect(() => {
    if (!active || !error) return;
    const ids = submitted.length ? submitted : rows.map((r) => r.itemId);
    const bad = ids.find((id) =>
      ['keepAtLeast', 'fillTo'].some((key) =>
        fieldErrorAt(error.message, ['proposal', 'points', ids.indexOf(id), key]),
      ),
    );
    if (bad) {
      const index = rows.findIndex((r) => r.itemId === bad);
      if (index >= 0) setPage(Math.floor(index / 100));
    }
    requestAnimationFrame(() =>
      root.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus(),
    );
  }, [active, error]);
  const submit = () => {
    if (
      !name.trim() ||
      rows.some(
        (p) =>
          !p.keep.trim() ||
          !p.fill.trim() ||
          numberTextIssue(p.keep, 'Keep') ||
          numberTextIssue(p.fill, 'Fill'),
      )
    ) {
      setLocalError('Enter a name and complete Keep/Fill pairs.');
      return;
    }
    setSubmitted(rows.map((r) => r.itemId));
    setLocalError(null);
    onReview({
      kind: 'edit',
      templateId: template.templateId,
      name,
      points: rows.map((p) => ({
        itemId: p.itemId,
        keepAtLeast: Number(p.keep),
        fillTo: Number(p.fill),
      })),
    });
  };
  return (
    <section ref={root} className="space-y-4">
      {error || localError ? (
        <Notice tone="danger" alert>
          {localError ?? fieldErrorOf(error!.message)}
        </Notice>
      ) : null}
      <fieldset disabled={saving} className="space-y-4">
        <Field label="Template name">
          <input
            className={inputClass}
            aria-label="Template name"
            required
            maxLength={120}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </Field>
        <p className="text-sm text-muted-foreground">
          Template pairs update inheriting vans. Local pairs remain Local. Review every
          affected van before confirming.
        </p>
        {!rows.length ? <p className="text-sm">No parts in this template yet.</p> : null}
        <ul className="divide-y divide-border">
          {rows.slice(page * 100, page * 100 + 100).map((p) => (
            <li key={p.itemId} className="space-y-2 py-3">
              <p className="break-words text-sm font-semibold">{p.itemName}</p>
              {!p.available ? (
                <p className="text-xs">
                  Unavailable part. Keep its pair unchanged or explicitly remove it.
                </p>
              ) : null}
              <div className="grid grid-cols-2 gap-2">
                {(['keep', 'fill'] as const).map((k) => {
                  const issue =
                      numberTextIssue(p[k], k === 'keep' ? 'Keep' : 'Fill') ??
                      fieldErrorAt(error?.message, [
                        'proposal',
                        'points',
                        submitted.length ? submitted.indexOf(p.itemId) : rows.indexOf(p),
                        k === 'keep' ? 'keepAtLeast' : 'fillTo',
                      ]) ??
                      (localError && !p[k].trim()
                        ? `Enter ${k === 'keep' ? 'Keep' : 'Fill'} for this part.`
                        : undefined),
                    id = `${fieldsId}-${p.itemId}-${k}`,
                    label = k === 'keep' ? 'Keep at least' : 'Fill to';
                  return (
                    <Field key={k} label={label}>
                      <input
                        className={inputClass}
                        aria-label={`${label} for ${p.itemName}`}
                        required
                        inputMode="decimal"
                        disabled={!p.available}
                        aria-invalid={!!issue}
                        aria-describedby={issue ? id : undefined}
                        value={p[k]}
                        onChange={(e) =>
                          setRows((r) =>
                            r.map((x) =>
                              x.itemId === p.itemId ? { ...x, [k]: e.target.value } : x,
                            ),
                          )
                        }
                      />
                      {issue ? (
                        <p id={id} role="alert">
                          {issue}
                        </p>
                      ) : null}
                    </Field>
                  );
                })}
              </div>
              <Button
                variant="ghost"
                className="min-h-11"
                aria-label={`Remove ${p.itemName} from template`}
                onClick={() => {
                  setRows((r) => r.filter((x) => x.itemId !== p.itemId));
                  setPage((n) =>
                    Math.min(n, Math.max(0, Math.ceil((rows.length - 1) / 100) - 1)),
                  );
                }}
              >
                Remove from template
              </Button>
            </li>
          ))}
        </ul>
        {rows.length > 100 ? (
          <div className="flex flex-wrap gap-2">
            <Button
              variant="ghost"
              disabled={page === 0}
              onClick={() => setPage((n) => n - 1)}
            >
              Previous parts
            </Button>
            <span>
              {page + 1} / {Math.ceil(rows.length / 100)}
            </span>
            <Button
              variant="ghost"
              disabled={(page + 1) * 100 >= rows.length}
              onClick={() => setPage((n) => n + 1)}
            >
              Next parts
            </Button>
          </div>
        ) : null}
        <Field label="Add a part">
          <select
            className={inputClass}
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
          >
            <option value="">Choose a part</option>
            {template.availableItems
              .filter((i) => !rows.some((r) => r.itemId === i.itemId))
              .map((i) => (
                <option key={i.itemId} value={i.itemId}>
                  {i.name}
                  {i.sku ? ` · ${i.sku}` : ''}
                </option>
              ))}
          </select>
        </Field>
        <Button
          variant="ghost"
          className="min-h-11"
          disabled={!selected || rows.length >= 5000}
          onClick={() => {
            const i = template.availableItems.find((i) => i.itemId === selected);
            if (!i) return;
            setRows((r) => [
              ...r,
              {
                itemId: i.itemId,
                itemName: i.name,
                available: true,
                keepAtLeastHundredths: 0,
                fillToHundredths: 0,
                keep: '',
                fill: '',
              },
            ]);
            setPage(Math.floor(rows.length / 100));
            setSelected('');
          }}
        >
          Add part
        </Button>
      </fieldset>
      <Button
        className="min-h-11 w-full"
        disabled={saving || !online || template.contractVersion !== 2}
        onClick={submit}
      >
        Review changes
      </Button>
    </section>
  );
}
